import axios from 'axios';

// Utility function for sorting versions (latest first, then by created_at)
const sortVersionsByLatestAndDate = (a, b) => {
  if (a.is_latest && !b.is_latest) return -1;
  if (!a.is_latest && b.is_latest) return 1;
  return new Date(b.created_at) - new Date(a.created_at);
};

// ============================================================================
// HARDCODED API URL FOR LOCAL DEVELOPMENT
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.plexe.ai';
// WebSocket URL for chat streaming
const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'wss://ahk91x3be9.execute-api.us-west-2.amazonaws.com/prod/';

// Create axios instance with default config for the main API
const api = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json'
  },
});

// Create a separate instance for auth endpoints
const authApi = axios.create({
  baseURL: '/auth',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 15000,
  withCredentials: false
});

// Override Content-Type header for FormData requests
api.interceptors.request.use(config => {
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Add auth headers to requests
const addAuthHeaders = (config) => {
  const apiKey = localStorage.getItem('apiKey');
  const accessToken = localStorage.getItem('accessToken');

  if (apiKey) {
    config.headers['x-api-key'] = apiKey;
  }

  if (accessToken) {
    config.headers['auth'] = accessToken.trim();
  }

  return config;
};

// Apply auth interceptor to all API instances
api.interceptors.request.use(addAuthHeaders, error => Promise.reject(error));
authApi.interceptors.request.use(addAuthHeaders, error => Promise.reject(error));

// Track recursion to prevent infinite loops
let isHandling403 = false;
// Track last redirect to prevent multiple redirects in quick succession
let lastRedirectTime = 0;
const REDIRECT_COOLDOWN_MS = 2000; // 2 seconds

// Handler for 403 errors to check if user has credits
export const handle403Error = async (error) => {
  // Only handle 403 errors
  if (error.response && error.response.status === 403) {
    // Prevent recursive handling - if we're already handling a 403, don't handle it again
    if (isHandling403) {
      console.log('Already handling a 403 error - skipping to prevent recursion');
      return { handled: false };
    }

    // Check if we have auth credentials - if not, don't try to make another API call
    const apiKey = localStorage.getItem('apiKey');
    const accessToken = localStorage.getItem('accessToken');

    if (!apiKey && !accessToken) {
      // Don't redirect if we're already on a public route (login, auth callback, etc.)
      const publicRoutes = ['/login', '/auth/callback', '/reset-password', '/share/', '/top-up-required'];
      const currentPath = window.location.pathname;
      const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));

      if (!isPublicRoute) {
        // Prevent multiple redirects in quick succession
        const now = Date.now();
        if (now - lastRedirectTime < REDIRECT_COOLDOWN_MS) {
          console.log('Skipping redirect - too soon after last redirect');
          return { handled: false };
        }
        lastRedirectTime = now;

        // User is not authenticated and not on a public route, redirect to login
        console.log('403 error with no auth credentials - redirecting to login');
        window.location.href = '/login';
        return { handled: true };
      }

      // On public routes, just log and return - let the error be caught by the caller
      console.log('403 error on public route - skipping redirect');
      return { handled: false };
    }

    // Set flag to prevent recursive calls
    isHandling403 = true;

    try {
      // Check user profile to see if they have credits
      const userProfile = await makeRequest({
        url: '/usage/profile',
        useProxy: false,
      });


      // If has_credits is explicitly false, redirect to usage page
      if (userProfile && userProfile.has_credits === false) {
        // Don't redirect if we're already on the usage page - this prevents refresh loops
        const currentPath = window.location.pathname;
        if (currentPath === '/usage' || currentPath.startsWith('/usage/')) {
          console.log('Already on usage page - not redirecting');
          return { handled: false };
        }

        // Set a flag in sessionStorage to show notification after navigation
        // This prevents multiple notifications and ensures it shows after navigation
        sessionStorage.setItem('showCreditNotification', 'true');

        // Navigate to usage page immediately
        window.location.href = '/usage';

        return { handled: true, userProfile };
      }
    } catch (profileError) {
      console.error('Error checking user credits:', profileError);
      // Don't try to redirect on nested errors - just fail gracefully
    } finally {
      // Always reset the flag
      isHandling403 = false;
    }
  }

  return { handled: false };
};

// Create a unified request function to handle any API request with consistent error handling
const makeRequest = async (options) => {
  // Default values
  const {
    method = 'GET',
    url,
    data = null,
    headers = {},
    useAuth = true,
    useProxy = true,
    retryWithDirectUrl = true,
    formData = false,
  } = options;

  console.log(`makeRequest: Starting ${method} request to ${url}, useProxy: ${useProxy}, useAuth: ${useAuth}`);

  // Build request config
  const config = {
    method,
    url,
    headers: { ...headers },
  };

  // Add data if provided
  if (data) {
    config.data = formData ? data : JSON.stringify(data);
  }

  // If this is FormData, don't set Content-Type
  if (formData) {
    delete config.headers['Content-Type'];
  } else {
    config.headers['Content-Type'] = 'application/json';
  }

  // Add auth headers if needed
  if (useAuth) {
    const apiKey = localStorage.getItem('apiKey');
    const accessToken = localStorage.getItem('accessToken');

    if (apiKey) {
      config.headers['x-api-key'] = apiKey;
      console.log('makeRequest: Added x-api-key header');
    }

    if (accessToken) {
      config.headers['auth'] = accessToken.trim();
      console.log('makeRequest: Added auth header');
    }
  }

  try {
    // Try proxy first if enabled
    if (useProxy) {
      try {
        console.log('makeRequest: Attempting proxy request with config:', config);
        const response = await axios(config);
        console.log('makeRequest: Proxy request successful, response status:', response.status);
        return response.data;
      } catch (proxyError) {
        console.log('makeRequest: Proxy request failed:', proxyError.message);

        // If we don't want to retry with direct URL or it's already a direct URL, throw
        if (!retryWithDirectUrl || url.startsWith('http')) {
          throw proxyError;
        }
      }
    }

    // If we get here, either proxy failed or useProxy is false
    // Try direct API URL
    const directUrl = url.startsWith('http')
      ? url
      : `${API_BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;

    config.url = directUrl;
    console.log('makeRequest: Attempting direct request to:', directUrl);

    try {
      const response = await axios(config);
      console.log('makeRequest: Direct request successful, response status:', response.status);
      return response.data;
    } catch (directError) {
      console.log('makeRequest: Direct request failed:', directError.message);
      throw directError;
    }
  } catch (error) {
    console.error('makeRequest: API request failed:', error);

    // Handle 403 errors and check if user has credits
    const { handled } = await handle403Error(error);
    if (handled) {
      // If the error was handled by the 403 handler, return null to prevent further processing
      return null;
    }

    throw error;
  }
};

// ===== API Service Modules =====

// Model Service
export const modelService = {
  // Get all models
  getModels: async () => {
    try {
      return await makeRequest({
        url: '/models',
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching models:', error);
      throw error;
    }
  },

  // Get model by ID
  getModel: async (modelId) => {
    try {
      let modelName, modelVersion;

      if (modelId.includes('/')) {
        [modelName, modelVersion] = modelId.split('/');
      } else {
        modelName = "model";
        modelVersion = modelId;
      }

      return await makeRequest({
        url: `/models/${modelName}/${modelVersion}`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error fetching model ${modelId}:`, error);
      return null;
    }
  },

  // Create a new model
  createModel: async (modelName, modelData) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/models/${modelName}`,
        data: modelData,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error creating model ${modelName}:`, error);
      throw error;
    }
  },

  // Get model status
  getModelStatus: async (modelName, modelVersion) => {
    try {
      return await makeRequest({
        url: `/models/${modelName}/${modelVersion}/status`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error fetching status for model ${modelName}/${modelVersion}:`, error);
      throw error;
    }
  },

  // Get model description
  getModelDescription: async (modelName, modelVersion) => {
    try {
      return await makeRequest({
        url: `/models/${modelName}/${modelVersion}/describe`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error fetching description for model ${modelName}/${modelVersion}:`, error);
      throw error;
    }
  },

  // Get model build logs
  getModelBuildLogs: async (modelId) => {
    try {
      let modelName, modelVersion;

      if (modelId.includes('/')) {
        [modelName, modelVersion] = modelId.split('/');
      } else {
        modelName = "model";
        modelVersion = modelId;
      }

      return await makeRequest({
        url: `/models/${modelName}/${modelVersion}/logs`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error fetching build logs for model ${modelId}:`, error);
      return null;
    }
  },

  // Run inference with a model
  predict: async (modelName, modelVersion, data) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/models/${modelName}/${modelVersion}/infer`,
        data,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error running inference with model ${modelName}/${modelVersion}:`, error);
      throw error;
    }
  },

  // Run batch inference with a model (multiple samples)
  predictBatch: async (modelName, modelVersion, dataArray) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/models/${modelName}/${modelVersion}/predictions`,
        data: dataArray,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error running batch inference with model ${modelName}/${modelVersion}:`, error);
      throw error;
    }
  },

  // Delete a model
  deleteModel: async (modelId) => {
    try {
      return await makeRequest({
        method: 'DELETE',
        url: `/models/models/${modelId}`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error deleting model ${modelId}:`, error);
      throw error;
    }
  },

  // Get all versions of a model (including original + retrained versions)
  getModelVersions: async (originalModelId) => {
    try {
      const allModels = await makeRequest({
        url: '/models',
        useProxy: false,
      });

      const versions = allModels.filter(model => {
        if (model.model_id === originalModelId) {
          return true;
        }

        if (model.model_description?.model_lineage?.parent_model === originalModelId ||
          model.model_description?.retraining_info?.original_experiment_id === originalModelId) {
          return true;
        }

        // Fallback: check if model name suggests it's a retrained version
        const originalModel = allModels.find(m => m.model_id === originalModelId);
        if (originalModel && model.model_name && originalModel.model_name) {
          const isRetrainedByName = model.model_name.includes(originalModel.model_name) &&
            model.model_name.includes('-retrain-');
          if (isRetrainedByName) {
            return true;
          }
        }

        return false;
      });

      versions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return versions;
    } catch (error) {
      console.error(`Error fetching versions for model ${originalModelId}:`, error);
      return [];
    }
  },

  // Retrain a model with new dataset
  retrainModel: async (originalModelId, datasetIds, modelName = null) => {
    try {
      // Get dataset details to extract correct filenames and data_dirs
      const datasets = [];
      for (const datasetId of datasetIds) {
        const uploadData = await uploadService.getUpload(datasetId);
        const filename = uploadData.files?.[0]?.filename || "data.csv";
        datasets.push({
          id: datasetId,
          file_name: filename,
          data_dirs: uploadData.data_dirs?.[0] || ""
        });
      }

      return await makeRequest({
        method: 'POST',
        url: `/models/${originalModelId}/retrain`,
        data: {
          datasets: datasets,
          model_name: modelName
        },
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error retraining model ${originalModelId}:`, error);
      throw error;
    }
  },

  // Rebuild a model with the same configuration
  rebuildModel: async (modelId, options = {}) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/models/models/${modelId}/rebuild`,
        data: options,  // Include resume_from_phase if provided
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error rebuilding model ${modelId}:`, error);
      throw error;
    }
  },

  // Get checkpoints for a model
  getCheckpoints: async (modelId) => {
    try {
      // Extract experiment_id from modelId (format: modelName/experimentId)
      const experimentId = modelId.includes('/') ? modelId.split('/')[1] : modelId;

      return await makeRequest({
        url: `/ml-models/experiments/${experimentId}/checkpoints`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error fetching checkpoints for ${modelId}:`, error);
      throw error;
    }
  },

  // Get detailed contents of a specific checkpoint
  getCheckpointDetails: async (modelId, phase) => {
    try {
      // Extract experiment_id from modelId (format: modelName/experimentId)
      const experimentId = modelId.includes('/') ? modelId.split('/')[1] : modelId;

      return await makeRequest({
        url: `/ml-models/experiments/${experimentId}/checkpoints/${phase}`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error fetching checkpoint details for ${modelId}/${phase}:`, error);
      throw error;
    }
  },

  // Update a specific object in a checkpoint
  updateCheckpoint: async (modelId, phase, object_key, new_value) => {
    try {
      // Extract experiment_id from modelId (format: modelName/experimentId)
      const experimentId = modelId.includes('/') ? modelId.split('/')[1] : modelId;

      return await makeRequest({
        method: 'PATCH',
        url: `/ml-models/experiments/${experimentId}/checkpoints/${phase}`,
        data: { object_key, new_value },
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error updating checkpoint for ${modelId}/${phase}:`, error);
      throw error;
    }
  },

  // Delete all checkpoints for a model
  deleteCheckpoints: async (modelId) => {
    try {
      // Extract experiment_id from modelId (format: modelName/experimentId)
      const experimentId = modelId.includes('/') ? modelId.split('/')[1] : modelId;

      return await makeRequest({
        method: 'DELETE',
        url: `/ml-models/experiments/${experimentId}/checkpoints`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error deleting checkpoints for ${modelId}:`, error);
      throw error;
    }
  },

  // Submit feedback for a paused experiment
  submitFeedback: async (experimentId, phase, feedback) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/ml-models/experiments/${experimentId}/feedback?phase=${phase}`,
        data: feedback,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error submitting feedback for ${experimentId}/${phase}:`, error);
      throw error;
    }
  },

  // Download model artifacts
  downloadModel: async (modelId) => {
    try {
      let modelName, modelVersion;

      if (modelId.includes('/')) {
        [modelName, modelVersion] = modelId.split('/');
      } else {
        modelName = "model";
        modelVersion = modelId;
      }

      const response = await makeRequest({
        method: 'GET',
        url: `/models/${modelName}/${modelVersion}/download`,
        useProxy: false,
      });

      // The response contains artifacts with presigned URLs
      if (response.artifacts && response.artifacts.length > 0) {
        // Download all artifacts using fetch to handle CORS properly
        for (const artifact of response.artifacts) {
          try {
            const fileResponse = await fetch(artifact.download_url);
            const blob = await fileResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = artifact.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (downloadError) {
            console.error(`Failed to download ${artifact.filename}:`, downloadError);
          }
        }
      }

      return response;
    } catch (error) {
      console.error(`Error downloading model ${modelId}:`, error);
      throw error;
    }
  },

  // Create share for a model
  createShare: async (modelId) => {
    try {
      let modelName, modelVersion;

      if (modelId.includes('/')) {
        [modelName, modelVersion] = modelId.split('/');
      } else {
        modelName = "model";
        modelVersion = modelId;
      }

      return await makeRequest({
        method: 'POST',
        url: `/models/${modelName}/${modelVersion}/share`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error creating share for model ${modelId}:`, error);
      throw error;
    }
  },

  // Get public model by share token (no auth required)
  getPublicModel: async (shareToken) => {
    try {
      return await makeRequest({
        method: 'GET',
        url: `/models/share/${shareToken}`,
        useAuth: false,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error fetching public model:`, error);
      throw error;
    }
  },

  // Update model visibility
  updateVisibility: async (modelId, visibility) => {
    try {
      let modelName, modelVersion;

      if (modelId.includes('/')) {
        [modelName, modelVersion] = modelId.split('/');
      } else {
        modelName = "model";
        modelVersion = modelId;
      }

      return await makeRequest({
        method: 'PUT',
        url: `/models/${modelName}/${modelVersion}/visibility`,
        data: { visibility },
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error updating visibility for model ${modelId}:`, error);
      throw error;
    }
  },

  // Revoke model share
  revokeShare: async (modelId) => {
    try {
      let modelName, modelVersion;

      if (modelId.includes('/')) {
        [modelName, modelVersion] = modelId.split('/');
      } else {
        modelName = "model";
        modelVersion = modelId;
      }

      return await makeRequest({
        method: 'DELETE',
        url: `/models/${modelName}/${modelVersion}/share`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error revoking share for model ${modelId}:`, error);
      throw error;
    }
  },
};

// Upload Service
export const uploadService = {
  // Get all uploads
  getUploads: async () => {
    try {
      return await makeRequest({
        url: '/uploads',
      });
    } catch (error) {
      console.error('Error fetching uploads:', error);
      return [];
    }
  },

  // Get datasets grouped by name
  getDatasets: async () => {
    try {
      const response = await makeRequest({
        url: '/uploads/datasets',
      });

      // Response already has datasets property with grouped structure
      if (response && response.datasets) {
        // Sort versions within each dataset (latest first, then by created_at)
        Object.keys(response.datasets).forEach(datasetName => {
          response.datasets[datasetName].sort(sortVersionsByLatestAndDate);
        });
        return response;
      }

      return { datasets: {} };
    } catch (error) {
      console.error('Error fetching datasets:', error);
      return { datasets: {} };
    }
  },

  // Get versions of a specific dataset
  getDatasetVersions: async (datasetName) => {
    try {
      const response = await makeRequest({
        url: `/uploads?dataset_name=${encodeURIComponent(datasetName)}`,
      });

      // Sort versions (latest first, then by created_at)
      if (Array.isArray(response)) {
        response.sort(sortVersionsByLatestAndDate);
      }

      return { versions: response || [] };
    } catch (error) {
      console.error(`Error fetching versions for dataset ${datasetName}:`, error);
      return { versions: [] };
    }
  },

  // Get upload by ID
  getUpload: async (uploadId) => {
    try {
      return await makeRequest({
        url: `/uploads/${uploadId}`,
        useAuth: true,
      });
    } catch (error) {
      console.error(`Error fetching upload ${uploadId}:`, error);
      return {};
    }
  },

  // Upload files using pre-signed S3 URL
  uploadFiles: async (files, versioningOptions = {}) => {
    // Validate auth tokens before attempting upload
    const apiKey = localStorage.getItem('apiKey');
    const accessToken = localStorage.getItem('accessToken');

    if (!apiKey && !accessToken) {
      throw new Error('Authentication required: Please log in again');
    }

    try {
      const results = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Step 1: Get pre-signed URL for this file
        const formData = new FormData();
        formData.append('filename', file.name);
        const contentType = 'application/octet-stream';
        formData.append('content_type', contentType);
        formData.append('file_size', file.size); // Required for upload limit validation

        // Add dataset versioning fields if provided
        if (versioningOptions.dataset_name) {
          formData.append('dataset_name', versioningOptions.dataset_name);
        }
        if (versioningOptions.parent_dataset_id) {
          formData.append('parent_dataset_id', versioningOptions.parent_dataset_id);
        }
        if (versioningOptions.version_description) {
          formData.append('version_description', versioningOptions.version_description);
        }

        let presignedResponse;
        let retryCount = 0;
        const maxRetries = 2;

        while (retryCount <= maxRetries) {
          try {
            presignedResponse = await makeRequest({
              method: 'POST',
              url: '/uploads',
              data: formData,
              formData: true,
            });
            break;
          } catch (error) {
            // Retry on auth failures
            if ((error.response?.status === 401 || error.response?.status === 403) && retryCount < maxRetries) {
              console.log(`Upload auth error, retrying... (${retryCount + 1}/${maxRetries})`);
              retryCount++;
              // Brief delay before retry
              await new Promise(resolve => setTimeout(resolve, 500));
            } else {
              throw error;
            }
          }
        }

        const { upload_id, presigned_url, key, validation_warnings } = presignedResponse;

        // Step 2: Upload file directly to S3 using the pre-signed URL with retry
        let s3RetryCount = 0;
        const maxS3Retries = 3;

        while (s3RetryCount <= maxS3Retries) {
          try {
            await axios({
              method: 'PUT',
              url: presigned_url,
              data: file,
              headers: {
                'Content-Type': 'application/octet-stream'
              },
              transformRequest: [(data) => data],
              timeout: 900000 // 15 minute timeout for large files (supports slow connections with 100MB uploads)
            });
            break;
          } catch (s3Error) {
            if (s3RetryCount < maxS3Retries &&
              (s3Error.code === 'NETWORK_ERROR' ||
                s3Error.code === 'ECONNABORTED' ||
                s3Error.code === 'ENOTFOUND' ||
                s3Error.code === 'ECONNRESET' ||
                s3Error.code === 'ETIMEDOUT' ||
                s3Error.response?.status >= 500)) {
              console.log(`S3 upload retry ${s3RetryCount + 1}/${maxS3Retries} for ${file.name}`);
              s3RetryCount++;
              await new Promise(resolve => setTimeout(resolve, 1000 * s3RetryCount)); // Exponential backoff
            } else {
              console.error('S3 upload error:', s3Error);
              throw s3Error;
            }
          }
        }

        // Step 3: Upload complete - validation will happen automatically in Lambda
        // No need to call /uploads/status - Lambda processes on S3 event
        results.push({
          upload_id: upload_id,
          filename: file.name,
          status: 'uploaded',  // File uploaded to S3, Lambda will process
          validation_warnings: validation_warnings || [],  // Pre-upload warnings only
        });
      }

      // Return combined result
      return {
        upload_id: results[0]?.upload_id || '',
        status: 'complete',
        message: `Successfully uploaded ${results.length} file(s)`,
        files: results.map(r => ({
          filename: r.filename,
          validation_warnings: r.validation_warnings,
          file_validation: r.file_validation
        })),
        validation_data: results // Keep full validation data for detailed display
      };
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  },

  // Upload large files - uses the same method as regular files
  uploadLargeFiles: async (files) => {
    return uploadService.uploadFiles(files);
  },

  // Delete upload
  deleteUpload: async (uploadId) => {
    try {
      return await makeRequest({
        method: 'DELETE',
        url: `/uploads/${uploadId}`,
      });
    } catch (error) {
      console.error(`Error deleting upload ${uploadId}:`, error);
      throw error;
    }
  },

  // Download dataset
  downloadDataset: async (uploadId) => {
    try {
      // Direct download - fetch as blob
      const config = {
        method: 'GET',
        url: `/uploads/${uploadId}/download`,
        responseType: 'blob'
      };

      const apiKey = localStorage.getItem('apiKey');
      const accessToken = localStorage.getItem('accessToken');

      const headers = {};
      if (apiKey) headers['x-api-key'] = apiKey;
      if (accessToken) headers['auth'] = accessToken.trim();

      const response = await axios({
        ...config,
        headers,
        url: `https://api.plexe.ai${config.url}`
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="?(.+?)"?$/);
      link.download = filenameMatch ? filenameMatch[1] : `dataset-${uploadId}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error(`Error downloading dataset ${uploadId}:`, error);
      throw error;
    }
  },
};

// Database Service
export const databaseService = {
  // Get all database connections
  getConnections: async () => {
    try {
      return await makeRequest({
        url: '/database/connections',
      });
    } catch (error) {
      console.error('Error fetching database connections:', error);
      return [];
    }
  },

  // Get specific database connection
  getConnection: async (connectionId) => {
    try {
      return await makeRequest({
        url: `/database/connections/${connectionId}`,
      });
    } catch (error) {
      console.error(`Error fetching database connection ${connectionId}:`, error);
      return {};
    }
  },

  // Create new database connection
  createConnection: async (connectionData) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/database/connections',
        data: connectionData,
      });
    } catch (error) {
      console.error('Error creating database connection:', error);
      throw error;
    }
  },

  // Delete database connection
  deleteConnection: async (connectionId) => {
    try {
      return await makeRequest({
        method: 'DELETE',
        url: `/database/connections/${connectionId}`,
      });
    } catch (error) {
      console.error(`Error deleting database connection ${connectionId}:`, error);
      throw error;
    }
  },

  // Test database connection
  testConnection: async (connectionData) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/database/connections/test',
        data: connectionData,
      });
    } catch (error) {
      console.error('Error testing database connection:', error);
      throw error;
    }
  },

  // Get all ingestions
  getIngestions: async () => {
    try {
      return await makeRequest({
        url: '/database/ingest',
      });
    } catch (error) {
      console.error('Error fetching database ingestions:', error);
      return [];
    }
  },

  // Get specific ingestion
  getIngestion: async (ingestionId) => {
    try {
      return await makeRequest({
        url: `/database/ingest/${ingestionId}`,
      });
    } catch (error) {
      console.error(`Error fetching database ingestion ${ingestionId}:`, error);
      return {};
    }
  },

  // Create new ingestion
  createIngestion: async (ingestionData) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/database/ingest',
        data: ingestionData,
      });
    } catch (error) {
      console.error('Error creating database ingestion:', error);
      throw error;
    }
  },

  // Retrigger ingestion
  retriggerIngestion: async (ingestionId) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/database/ingest/${ingestionId}/retrigger`,
      });
    } catch (error) {
      console.error(`Error retriggering database ingestion ${ingestionId}:`, error);
      throw error;
    }
  },
};

// Health Service
export const healthService = {
  checkHealth: async () => {
    try {
      return await makeRequest({
        url: '/health',
      });
    } catch (error) {
      console.error('Error checking health:', error);
      return { status: "OK" }; // Default fallback
    }
  },
};

// Auth Service
export const authService = {
  // Logout
  logout: async (refreshToken) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/auth/logout',
        data: { refresh_token: refreshToken },
        useProxy: false,
      });
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  },

  // Refresh token
  refreshToken: async (refreshToken) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/auth/refresh',
        data: { refresh_token: refreshToken },
        useAuth: false,
        useProxy: false,
      });
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  },

  // Get user info
  getCurrentUser: async () => {
    try {
      return await makeRequest({
        url: '/auth/user',
        useProxy: false,
      });
    } catch (error) {
      console.error('Get user error:', error);
      throw error;
    }
  },

  // Get API key (legacy method)
  getApiKey: async () => {
    try {
      return await makeRequest({
        url: '/auth/api-key',
        useProxy: false,
      });
    } catch (error) {
      console.error('Get API key error:', error);
      throw error;
    }
  },

  // List all API keys for the current user
  listApiKeys: async () => {
    try {
      return await makeRequest({
        url: '/auth/api-keys',
        useProxy: false,
      });
    } catch (error) {
      console.error('List API keys error:', error);
      throw error;
    }
  },

  // Create a new API key
  createApiKey: async (name = 'API Key') => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/auth/api-keys',
        data: { name },
        useProxy: false,
      });
    } catch (error) {
      console.error('Create API key error:', error);
      throw error;
    }
  },

  // Delete an API key
  deleteApiKey: async (keyId) => {
    try {
      return await makeRequest({
        method: 'DELETE',
        url: `/auth/api-keys/${keyId}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Delete API key error:', error);
      throw error;
    }
  },
};

// Data Service
export const dataService = {
  // Create a session for a dataset
  createSession: async (uploadId) => {
    try {
      if (!uploadId) {
        throw new Error('Upload ID is required');
      }

      const formData = new FormData();
      formData.append('upload_id', uploadId);

      const response = await makeRequest({
        method: 'POST',
        url: '/data/use-upload',
        data: formData,
        formData: true
      });

      if (!response || !response.session_id) {
        throw new Error('Failed to create data session: Invalid response');
      }

      return response;
    } catch (error) {
      console.error('Error creating data session:', error);
      throw error;
    }
  },

  // Query dataset with an existing session ID
  queryWithSession: async (sessionId, query) => {
    try {
      if (!sessionId || !query) {
        throw new Error('Session ID and query are required');
      }

      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('query', query);

      const headers = {
        'Accept': 'application/json'
      };

      return await makeRequest({
        method: 'POST',
        url: '/data/query',
        data: formData,
        formData: true,
        headers: headers,
        timeout: 30000
      });
    } catch (error) {
      console.error('Error in data query:', error);
      throw error;
    }
  },

  // Legacy method - query a dataset (creates a new session each time)
  queryDataset: async (uploadId, query) => {
    try {
      const sessionResult = await dataService.createSession(uploadId);
      return await dataService.queryWithSession(sessionResult.session_id, query);
    } catch (error) {
      console.error('Error in data query:', error);
      throw error;
    }
  },

  // Get dataset schema
  getDatasetSchema: async (uploadId) => {
    try {
      if (!uploadId) {
        throw new Error('Upload ID is required');
      }

      const response = await makeRequest({
        method: 'GET',
        url: `/data/schema/${uploadId}`,
      });

      return response;
    } catch (error) {
      console.error('Error fetching dataset schema:', error);
      throw error;
    }
  },

  // Add columns to a dataset using AI
  addColumnsToDataset: async (uploadId, fileName, columns, description, saveAsNew = true) => {
    try {
      if (!uploadId) {
        throw new Error('Upload ID is required');
      }

      if (!columns || Object.keys(columns).length === 0) {
        throw new Error('At least one column definition is required');
      }

      const response = await makeRequest({
        method: 'POST',
        url: '/data-generation',
        data: {
          upload_id: uploadId,
          file_name: fileName,
          columns: columns,
          description: description,
          save_as_new: saveAsNew
        }
      });

      return response;
    } catch (error) {
      console.error('Error adding columns to dataset:', error);
      throw error;
    }
  },

  // Legacy methods - Generate column preview using LLM
  generateColumnPreview: async (uploadId, columnConfig) => {
    try {
      if (!uploadId || !columnConfig) {
        throw new Error('Upload ID and column configuration are required');
      }

      return await makeRequest({
        method: 'POST',
        url: `/data/generate-column/${uploadId}/preview`,
        data: columnConfig
      });
    } catch (error) {
      console.error('Error generating column preview:', error);
      throw error;
    }
  },

  // Legacy method - Apply generated column to dataset
  applyGeneratedColumn: async (uploadId, columnConfig) => {
    try {
      if (!uploadId || !columnConfig) {
        throw new Error('Upload ID and column configuration are required');
      }

      return await makeRequest({
        method: 'POST',
        url: `/data/generate-column/${uploadId}/apply`,
        data: columnConfig
      });
    } catch (error) {
      console.error('Error applying generated column:', error);
      throw error;
    }
  },

  // Get visualization URL with support for presigned S3 URLs
  getVisualizationWithS3Support: (visualization, visualizationType) => {
    if (visualizationType === 's3_url' && visualization.startsWith('http')) {
      return visualization;
    }

    if (visualization.startsWith('http')) {
      return visualization;
    }

    return dataService.getVisualization(visualization);
  },

  // Test connection
  testConnection: async () => {
    try {
      return await makeRequest({
        url: '/data/health',
      });
    } catch (error) {
      console.error('Data service health check failed:', error);
      throw error;
    }
  },

  // Get rich dataset preview with statistics and caching
  getDatasetPreview: async (datasetId, version = null, quick = false) => {
    try {
      if (!datasetId) {
        throw new Error('Dataset ID is required');
      }

      // Check cache first (6 hours)
      const cacheKey = `dataset_preview_${datasetId}_${version || 'latest'}_${quick}`;
      const cached = getCachedData(cacheKey, 6 * 60 * 60 * 1000); // 6 hours in ms
      if (cached) {
        console.log('Using cached dataset preview for:', datasetId);
        return cached;
      }

      const params = new URLSearchParams();
      if (version) params.append('version', version);
      if (quick !== null) params.append('quick', quick.toString());

      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await makeRequest({
        method: 'GET',
        url: `/datasets/${datasetId}/preview${queryString}`,
        useProxy: false,
      });

      // Cache successful response
      setCachedData(cacheKey, response);
      console.log('Cached dataset preview for:', datasetId);

      return response;
    } catch (error) {
      console.error('Error fetching dataset preview:', error);
      throw error;
    }
  }
};

// Dataset Preview Caching Utilities (6-hour cache)
const CACHE_KEY_PREFIX = 'dataset_preview_';

const getCachedData = (cacheKey, maxAgeMs) => {
  try {
    const cached = sessionStorage.getItem(`${CACHE_KEY_PREFIX}${cacheKey}`);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < maxAgeMs) {
        return data;
      }
      // Remove expired cache
      sessionStorage.removeItem(`${CACHE_KEY_PREFIX}${cacheKey}`);
    }
  } catch (error) {
    console.warn('Cache read error:', error);
  }
  return null;
};

const setCachedData = (cacheKey, data) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now()
    };
    sessionStorage.setItem(`${CACHE_KEY_PREFIX}${cacheKey}`, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Cache write error:', error);
  }
};

// Usage Service
export const usageService = {
  // Get user profile
  getUserProfile: async () => {
    try {
      return await makeRequest({
        url: '/usage/profile',
        useProxy: false,
      });

    } catch (error) {
      console.error('Error fetching user profile:', error);
      return {
        user_id: '',
        email: '',
        name: '',
        picture: null,
        usage: {
          consumption: 0,
          credits: 0,
          updated_at: new Date().toISOString()
        },
        has_made_top_up: false
      };
    }
  },

  // Get user profile with usage data
  getUserProfileWithUsage: async () => {
    try {
      return await makeRequest({
        url: '/usage/api/profile',
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching user profile with usage:', error);
      return {};
    }
  },

  // Get current usage data
  getCurrentUsage: async () => {
    try {
      return await makeRequest({
        url: '/usage/api/current',
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching current usage:', error);
      return {};
    }
  },

  // Get account balance
  getBalance: async () => {
    try {
      // Add cache-busting parameter to prevent caching
      return await makeRequest({
        url: `/usage/balance?_t=${Date.now()}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching account balance:', error);
      return {
        "available_balance": 0,
        "reported_balance": 0,
        "unbilled_usage": 0,
        "currency": "usd",
        "updated_at": "2025-09-08T11:31:47.712000+00:00"
      };
    }
  },

  // Get usage breakdown by resource type
  getUsageBreakdown: async () => {
    try {
      return await makeRequest({
        url: '/usage/breakdown',
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching usage breakdown:', error);
      // Return mock data for development
      return {
        total: 0.0,
        resources: [
          { type: 'chat', name: 'Chat Usage', input_tokens: 0, output_tokens: 0, cost: 0 },
          { type: 'storage', name: 'Storage', dataset_storage: 0, model_storage: 0, cost: 0 },
          { type: 'compute', name: 'Compute', inference_hours: 0, build_hours: 0, cost: 0 },
          { type: 'model_building', name: 'Model Building', input_tokens: 0, output_tokens: 0, cost: 0 }
        ]
      };
    }
  },

  // Get token usage history
  getTokenUsageHistory: async (period = 'month') => {
    try {
      return await makeRequest({
        url: `/usage/tokens?period=${period}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching token usage history:', error);
      return {
        history: []
      };
    }
  },

  // Get hourly usage data for a specific day
  getHourlyUsageData: async (startTime, endTime) => {
    try {
      console.log(`Fetching usage data from ${startTime} to ${endTime}`);
      return await makeRequest({
        url: `/usage/?window=hour&start_time=${startTime}&end_time=${endTime}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching hourly usage data:', error);
      return {
        meters: {
          chat_input_tokens: { total: 0, data_points: [] },
          chat_output_tokens: { total: 0, data_points: [] },
          model_build_compute_hours: { total: 0, data_points: [] },
          model_build_input_tokens: { total: 0, data_points: [] },
          model_build_output_tokens: { total: 0, data_points: [] },
          model_inference_compute_hours: { total: 0, data_points: [] },
          model_inference_invocation_seconds: { total: 0, data_points: [] },
          model_inference_third_party_invocations: { total: 0, data_points: [] },
          model_storage_gb: { total: 0, data_points: [] },
          dataset_storage_gb: { total: 0, data_points: [] }
        }
      };
    }
  },

  // Get payment history
  getPaymentHistory: async () => {
    try {
      return await makeRequest({
        url: '/payment/history',
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  },
};

// Payment Service
export const paymentService = {
  // Process payment/top-up
  processPayment: async (amount, promoCode = null) => {
    try {
      // Prepare request data
      let requestData = {};

      if (promoCode) {
        requestData = { promo_code: promoCode };
      } else {
        // Convert amount to cents (integer)
        // e.g., 12.20 -> 1220, 12 -> 1200
        const amountInCents = Math.round(parseFloat(amount) * 100);
        requestData = { amount: amountInCents };
      }

      return await makeRequest({
        method: 'POST',
        url: '/payment/process',
        data: requestData,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  }
};

// WebSocket Chat Service§
class WebSocketChatService {
  constructor() {
    this.ws = null;
    this.wsUrl = WS_BASE_URL;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.messageHandlers = new Map();
    this.connectionPromise = null;
    this.messageTimeout = 5 * 60 * 1000; // 5 minutes for long-running operations like visualizations
    this.pingInterval = 30000; // Send ping every 30 seconds to keep connection alive
    this.pingTimer = null;
    this.outboundMessageHandler = null; // Global handler for async outbound messages
    this.notificationHandler = null; // Global handler for notifications
  }

  async connect() {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      const apiKey = localStorage.getItem('apiKey');
      if (!apiKey) {
        reject(new Error('API key not found in localStorage'));
        return;
      }

      const wsUrlWithAuth = `${this.wsUrl}?x-api-key=${apiKey}`;

      try {
        this.ws = new WebSocket(wsUrlWithAuth);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.reconnectAttempts = 0;
          this.startPingTimer();
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('WebSocket received:', data);
            this.handleMessage(data);
          } catch (error) {
            // Handle raw text messages (streaming chunks) - this is expected for tool execution logs
            console.log('WebSocket received raw:', event.data);
            this.handleMessage({ type: 'chunk', data: event.data });
          }
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          this.connectionPromise = null;

          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.connectionPromise = null;
          reject(error);
        };
      } catch (error) {
        this.connectionPromise = null;
        reject(error);
      }
    });

    return this.connectionPromise;
  }

  async reconnect() {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.connect().catch(error => {
        console.error('Reconnection failed:', error);
      });
    }, delay);
  }

  handleMessage(data) {
    // Handle outbound messages from backend services (async, not tied to user request)
    if (data.event === 'outbound_message') {
      if (this.outboundMessageHandler) {
        this.outboundMessageHandler(data.data);
      }
      return;
    }

    // Handle new message notifications for inactive sessions
    if (data.event === 'new_message_notification') {
      if (this.notificationHandler) {
        this.notificationHandler(data.data);
      }
      return;
    }

    // Handle completion status
    if (data.status === 'complete') {
      this.messageHandlers.forEach((handler, handlerId) => {
        if (handler.timeout) {
          clearTimeout(handler.timeout);
        }
        if (handler.onComplete) {
          handler.onComplete();
        }
      });
      // Clear handlers after completion to prevent stale handlers from processing subsequent errors
      this.messageHandlers.clear();
      return;
    }

    // Handle error messages
    if (data.error) {
      this.messageHandlers.forEach(handler => {
        if (handler.onError) {
          handler.onError(data.error);
        }
      });
      return;
    }

    // Handle LangGraph streaming messages as chunks
    if (data.event === 'streaming_update' || data.event === 'message' || data.event === 'message_stream') {
      const chunk = JSON.stringify(data);
      this.messageHandlers.forEach(handler => {
        if (handler.onChunk) {
          handler.onChunk(chunk);
        }
      });
      return;
    }

    // Handle streaming chunks (legacy format)
    if (data.type === 'chunk' || typeof data === 'string') {
      const chunk = data.type === 'chunk' ? data.data : data;
      this.messageHandlers.forEach(handler => {
        if (handler.onChunk) {
          handler.onChunk(chunk);
        }
      });
      return;
    }

    // Handle structured messages (tool calls, etc.)
    this.messageHandlers.forEach(handler => {
      if (handler.onMessage) {
        handler.onMessage(data);
      }
    });
  }

  setOutboundMessageHandler(handler) {
    this.outboundMessageHandler = handler;
  }

  setNotificationHandler(handler) {
    this.notificationHandler = handler;
  }

  async setActiveSession(sessionId) {
    try {
      await this.connect();

      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          action: 'set_active_session',
          session_id: sessionId
        }));
        console.log('Set active session:', sessionId);
      }
    } catch (error) {
      console.error('Failed to set active session:', error);
    }
  }

  async setMode(sessionId, autoMode) {
    try {
      await this.connect();

      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({
          action: 'set_mode',
          session_id: sessionId,
          auto_mode: autoMode
        }));
        console.log('Set mode:', sessionId, autoMode);
      }
    } catch (error) {
      console.error('Failed to set mode:', error);
    }
  }

  async sendMessage(message, sessionId = null, questionId = null, handlers = {}) {
    await this.connect();

    if (this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected');
    }

    // Generate unique handler ID with timestamp and random component
    const handlerId = `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

    // Clear any existing handlers before adding new one to prevent conflicts
    // This ensures only one active streaming handler at a time
    this.messageHandlers.clear();

    this.messageHandlers.set(handlerId, {
      ...handlers,
      sessionId: sessionId, // Store session ID for handler isolation
      messageId: handlerId, // Store message ID for tracking
      timeout: setTimeout(() => {
        console.warn(`Message timeout reached for handler ${handlerId}, but keeping connection open for long-running operations`);
        // Don't automatically fail - let visualization complete
      }, this.messageTimeout)
    });

    const messageData = {
      action: 'chat',
      message: message,
      session_id: sessionId || '',
      message_id: handlerId // Include message ID in the request
    };

    // Add question_id if answering a question from model builder
    if (questionId) {
      messageData.question_id = questionId;
    }

    console.log(`Starting new message stream with handler ID: ${handlerId}${questionId ? ` (answering question ${questionId})` : ''}`);
    this.ws.send(JSON.stringify(messageData));

    // Return handler ID for cleanup
    return handlerId;
  }

  startPingTimer() {
    this.stopPingTimer();
    this.pingTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, this.pingInterval);
  }

  stopPingTimer() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  removeHandler(handlerId) {
    const handler = this.messageHandlers.get(handlerId);
    if (handler && handler.timeout) {
      clearTimeout(handler.timeout);
    }
    const removed = this.messageHandlers.delete(handlerId);
    if (removed) {
      console.log(`Removed handler: ${handlerId}`);
    }
  }

  disconnect() {
    this.stopPingTimer();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
    }
    this.connectionPromise = null;
    this.messageHandlers.clear();
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

// Create singleton instance
const webSocketChatService = new WebSocketChatService();

// Chat Service
export const chatService = {
  // Legacy HTTP method for backward compatibility
  sendMessage: async (message, sessionId = null) => {
    try {
      const requestBody = {
        message: message,
        session_id: sessionId
      };

      return await makeRequest({
        method: 'POST',
        url: '/chat',
        data: requestBody,
        useProxy: false
      });
    } catch (error) {
      console.error('Chat error:', error);
      throw error;
    }
  },

  // New WebSocket streaming method
  sendMessageStreaming: async (message, sessionId = null, questionId = null, handlers = {}) => {
    try {
      return await webSocketChatService.sendMessage(message, sessionId, questionId, handlers);
    } catch (error) {
      console.error('WebSocket chat error:', error);
      throw error;
    }
  },

  // WebSocket service management
  connect: () => webSocketChatService.connect(),
  disconnect: () => webSocketChatService.disconnect(),
  isConnected: () => webSocketChatService.isConnected(),
  removeHandler: (handlerId) => webSocketChatService.removeHandler(handlerId),
  setActiveSession: (sessionId) => webSocketChatService.setActiveSession(sessionId),
  setMode: (sessionId, autoMode) => webSocketChatService.setMode(sessionId, autoMode),
  setAutoMode: async (sessionId, autoMode) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/ml-models/chat/set-mode',
        params: { session_id: sessionId, auto_mode: autoMode },
        useProxy: false
      });
    } catch (error) {
      console.error('Error setting auto mode:', error);
      throw error;
    }
  },
  setOutboundMessageHandler: (handler) => webSocketChatService.setOutboundMessageHandler(handler),
  setNotificationHandler: (handler) => webSocketChatService.setNotificationHandler(handler),

  getVisualizationStatus: async (jobId) => {
    try {
      return await makeRequest({
        method: 'GET',
        url: `/visualization/${jobId}`,
        useProxy: false
      });
    } catch (error) {
      console.error('Visualization status error:', error);
      throw error;
    }
  },

  // Session Management Methods
  createChatSession: async (sessionTitle = "New Chat", sessionType = "general_chat") => {
    try {
      const requestData = {
        session_title: sessionTitle,
        session_type: sessionType
      };
      console.log('[DEBUG-CHAT] API call: POST /sessions', requestData);
      const response = await makeRequest({
        method: 'POST',
        url: '/sessions',
        data: requestData,
        useProxy: false
      });
      console.log('[DEBUG-CHAT] API response:', response);
      return response;
    } catch (error) {
      console.error('[DEBUG-CHAT] API error creating chat session:', error);
      throw error;
    }
  },

  getChatSessions: async () => {
    try {
      return await makeRequest({
        url: '/sessions',
        useProxy: false
      });
    } catch (error) {
      console.error('Error fetching chat sessions:', error);
      throw error;
    }
  },

  getChatSession: async (sessionId) => {
    try {
      return await makeRequest({
        url: `/sessions/${sessionId}`,
        useProxy: false
      });
    } catch (error) {
      console.error('Error fetching chat session:', error);
      throw error;
    }
  },

  updateChatSessionTitle: async (sessionId, title) => {
    try {
      return await makeRequest({
        method: 'PUT',
        url: `/sessions/${sessionId}/title`,
        data: { title },
        useProxy: false
      });
    } catch (error) {
      console.error('Error updating session title:', error);
      throw error;
    }
  },

  deleteChatSession: async (sessionId) => {
    try {
      return await makeRequest({
        method: 'DELETE',
        url: `/sessions/${sessionId}`,
        useProxy: false
      });
    } catch (error) {
      console.error('Error deleting chat session:', error);
      throw error;
    }
  }
};

// Batch Inference Service
export const batchInferenceService = {
  // Get all batch jobs for a model
  getBatchJobs: async () => {
    try {
      return await makeRequest({
        method: 'GET',
        url: `/batch-inference-jobs`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching batch jobs:', error);
      throw error;
    }
  },

  // Create a new batch job
  createBatchJob: async (modelId, datasetId) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/batch-inference-jobs`,
        data: {
          model_id: modelId,
          dataset_id: datasetId
        },
        useProxy: false,
      });
    } catch (error) {
      console.error('Error creating batch job:', error);
      throw error;
    }
  },

  // Get batch job status
  getBatchJobStatus: async (jobId) => {
    try {
      return await makeRequest({
        method: 'GET',
        url: `/batch-inference-jobs/${jobId}`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error fetching batch job ${jobId}:`, error);
      throw error;
    }
  }
};

// Dashboard Service
export const dashboardService = {
  // Get all dashboards
  getDashboards: async () => {
    try {
      return await makeRequest({
        url: '/dashboard',
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching dashboards:', error);
      throw error;
    }
  },

  // Get guest token for dashboard embedding
  getGuestToken: async (dashboardId) => {
    try {
      return await makeRequest({
        url: `/dashboard/${dashboardId}/guest_token`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error fetching guest token for dashboard ${dashboardId}:`, error);
      throw error;
    }
  },

  // Get embedding UUID for dashboard (used with Superset embedded SDK)
  getEmbeddingUuid: async (dashboardId) => {
    try {
      return await makeRequest({
        url: `/dashboard/${dashboardId}/embedding_uuid`,
        useProxy: false,
      });
    } catch (error) {
      console.error(`Error fetching embedding UUID for dashboard ${dashboardId}:`, error);
      throw error;
    }
  },
};

// S3 Connector Service
export const s3Service = {
  // Create S3 connection
  createConnection: async (connectionData) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/s3/connections',
        data: connectionData,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error creating S3 connection:', error);
      throw error;
    }
  },

  // List S3 connections
  listConnections: async () => {
    try {
      return await makeRequest({
        method: 'GET',
        url: '/s3/connections',
        useProxy: false,
      });
    } catch (error) {
      console.error('Error listing S3 connections:', error);
      throw error;
    }
  },

  // Get S3 connection
  getConnection: async (connectionId) => {
    try {
      return await makeRequest({
        method: 'GET',
        url: `/s3/connections/${connectionId}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error getting S3 connection:', error);
      throw error;
    }
  },

  // Delete S3 connection
  deleteConnection: async (connectionId) => {
    try {
      return await makeRequest({
        method: 'DELETE',
        url: `/s3/connections/${connectionId}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error deleting S3 connection:', error);
      throw error;
    }
  },

  // Test S3 connection
  testConnection: async (connectionData) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/s3/connections/test',
        data: connectionData,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error testing S3 connection:', error);
      throw error;
    }
  },

  // Browse S3 files
  browseFiles: async (connectionId, prefix = null, maxKeys = 100) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/s3/browse',
        data: {
          connection_id: connectionId,
          prefix: prefix,
          max_keys: maxKeys
        },
        useProxy: false,
      });
    } catch (error) {
      console.error('Error browsing S3 files:', error);
      throw error;
    }
  },

  // Import S3 files
  importFiles: async (connectionId, fileKeys, uploadName) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/s3/import',
        data: {
          connection_id: connectionId,
          file_keys: fileKeys,
          upload_name: uploadName
        },
        useProxy: false,
      });
    } catch (error) {
      console.error('Error importing S3 files:', error);
      throw error;
    }
  }
};

// Connector service for Fivetran integration
export const connectorService = {
  // Get available connector types
  getConnectorTypes: async (category = null, search = null) => {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (search) params.append('search', search);

      return await makeRequest({
        method: 'GET',
        url: `/connectors/types?${params.toString()}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching connector types:', error);
      throw error;
    }
  },

  // Create Connect Card for embedded setup
  createConnectCard: async (serviceType, redirectUri = `${window.location.origin}/data-sources`) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/connectors/connect-cards',
        data: {
          service: serviceType,
          redirect_uri: redirectUri
        },
        useProxy: false,
      });
    } catch (error) {
      console.error('Error creating Connect Card:', error);
      throw error;
    }
  },

  // List user's connectors
  getConnectors: async () => {
    try {
      return await makeRequest({
        method: 'GET',
        url: '/connectors',
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching connectors:', error);
      throw error;
    }
  },

  // Get connector details
  getConnector: async (connectorId) => {
    try {
      return await makeRequest({
        method: 'GET',
        url: `/connectors/${connectorId}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching connector:', error);
      throw error;
    }
  },

  // Trigger connector sync
  syncConnector: async (connectorId, force = false) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/connectors/${connectorId}/sync`,
        data: { force },
        useProxy: false,
      });
    } catch (error) {
      console.error('Error syncing connector:', error);
      throw error;
    }
  },

  // Delete connector
  deleteConnector: async (connectorId) => {
    try {
      return await makeRequest({
        method: 'DELETE',
        url: `/connectors/${connectorId}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error deleting connector:', error);
      throw error;
    }
  },

  // Mintsoft-specific methods
  createMintsoftConnection: async (connectionData) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/connectors/mintsoft/connections',
        data: connectionData,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error creating Mintsoft connection:', error);
      throw error;
    }
  },

  listMintsoftConnections: async () => {
    try {
      return await makeRequest({
        method: 'GET',
        url: '/connectors/mintsoft/connections',
        useProxy: false,
      });
    } catch (error) {
      console.error('Error listing Mintsoft connections:', error);
      throw error;
    }
  },

  syncMintsoftConnection: async (connectionId) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/connectors/mintsoft/connections/${connectionId}/sync`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error syncing Mintsoft connection:', error);
      throw error;
    }
  },

  deleteMintsoftConnection: async (connectionId) => {
    try {
      return await makeRequest({
        method: 'DELETE',
        url: `/connectors/mintsoft/connections/${connectionId}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error deleting Mintsoft connection:', error);
      throw error;
    }
  }
};

// Data Transformation Service
export const dataTransformationService = {
  // Get all transformation jobs
  getJobs: async (limit = 50) => {
    try {
      return await makeRequest({
        method: 'GET',
        url: `/data-transformations/jobs?limit=${limit}`,
        useProxy: false,
      });

    } catch (error) {
      console.error('Error fetching transformation jobs:', error);
      throw error;
    }
  },

  // Get job details
  getJobDetails: async (jobId) => {
    try {
      return await makeRequest({
        method: 'GET',
        url: `/data-transformations/jobs/${jobId}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching job details:', error);
      throw error;
    }
  },

  // Create a new transformation job
  createJob: async (jobData) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: '/data-transformations/jobs',
        data: jobData,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error creating transformation job:', error);
      throw error;
    }
  },

  // Approve generated code
  approveCode: async (jobId, editedCode = null) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/data-transformations/jobs/${jobId}/approve`,
        data: { edited_code: editedCode },
        useProxy: false,
      });
    } catch (error) {
      console.error('Error approving code:', error);
      throw error;
    }
  },

  // Reject code with feedback
  rejectCode: async (jobId, feedback) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/data-transformations/jobs/${jobId}/reject`,
        data: { feedback },
        useProxy: false,
      });
    } catch (error) {
      console.error('Error rejecting code:', error);
      throw error;
    }
  },

  // Cancel a job (only for executing jobs)
  cancelJob: async (jobId) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/data-transformations/jobs/${jobId}/cancel`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error cancelling job:', error);
      throw error;
    }
  },

  // Delete a job
  deleteJob: async (jobId) => {
    try {
      return await makeRequest({
        method: 'DELETE',
        url: `/data-transformations/jobs/${jobId}`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error deleting job:', error);
      throw error;
    }
  },

  // Retry a failed job
  retryJob: async (jobId, data) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/data-transformations/jobs/${jobId}/retry`,
        data,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error retrying job:', error);
      throw error;
    }
  },

  // Execute transformation (when implemented)
  getJobRuns: async (jobId) => {
    try {
      return await makeRequest({
        method: 'GET',
        url: `/data-transformations/jobs/${jobId}/runs`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error fetching job runs:', error);
      throw error;
    }
  },

  executeJob: async (jobId) => {
    try {
      return await makeRequest({
        method: 'POST',
        url: `/data-transformations/jobs/${jobId}/execute`,
        useProxy: false,
      });
    } catch (error) {
      console.error('Error executing job:', error);
      throw error;
    }
  }
};

// Feature Service
export const featureService = {
  // Cache for feature flags
  _cache: {
    features: null,
    cacheExpiry: null,
    cacheDuration: 5 * 60 * 1000 // 5 minutes
  },

  // Check if user has access to a specific feature
  checkFeature: async (featureName) => {
    try {
      const response = await makeRequest({
        url: `/features/check?feature=${featureName}`,
        useProxy: false
      });
      return response.access || false;
    } catch (error) {
      console.error(`Error checking feature ${featureName}:`, error);
      return false; // Default to no access on error
    }
  },

  // Check access to multiple features at once
  checkMultipleFeatures: async (featureNames) => {
    try {
      const response = await makeRequest({
        method: 'POST',
        url: '/features/multiple/check',
        data: { features: featureNames },
        useProxy: false
      });
      return response || {};
    } catch (error) {
      console.error('Error checking multiple features:', error);
      return {};
    }
  },

  // Get all features and their access status for the user
  getAllFeatures: async (forceRefresh = false) => {
    const cache = featureService._cache;

    // Check cache
    if (!forceRefresh && cache.features && cache.cacheExpiry && Date.now() < cache.cacheExpiry) {
      return cache.features;
    }

    try {
      const features = await makeRequest({
        url: '/features/all',
        useProxy: false
      });

      // Update cache
      cache.features = features || {};
      cache.cacheExpiry = Date.now() + cache.cacheDuration;

      return cache.features;
    } catch (error) {
      console.error('Error fetching all features:', error);
      return cache.features || {}; // Return cached version or empty
    }
  },

  // Helper method to check if a feature is enabled
  isFeatureEnabled: async (featureName) => {
    const features = await featureService.getAllFeatures();
    return features[featureName] || false;
  },

  // Clear cache
  clearCache: () => {
    featureService._cache.features = null;
    featureService._cache.cacheExpiry = null;
  }
};

export default api;
